// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useMemo, useRef, useState } from 'react';
import {
  StyleSheet,
  Text,
  View,
  type GestureResponderEvent,
  type LayoutChangeEvent,
} from 'react-native';

import Svg, { Circle, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';

import { useI18n } from '@/i18n';
import appColors, { alpha } from '@/theme/colors';

import { buildHoleGuide, type GuideFeature } from './holeGuide';
import { DRAWING_FILLS as FILLS, ROUGH_FILL } from './holeShapes';
import { pressPointInElement, type MeasurableElement, type PressPointEvent } from './pressPoint';
import { MAX_VIEW_ZOOM, MIN_VIEW_ZOOM, pointInPolygon, type Point } from './tileMath';
import { ZoomControls } from './ZoomControls';

/**
 * A printed-course-guide drawing of one hole, from the derived boundaries alone, with the
 * lay-up arcs a paper yardage book prints.
 *
 * The analysis view's copy of the drawing. Every shape on it comes from a colour threshold
 * over satellite imagery — the fairway edge separated from rough by mowing height, the tree
 * line a shadow gradient — and none of it is reviewed by a human, which is why the card
 * carries that caveat in writing. The hole view offers a plainer version of the same picture
 * to players; the arcs and the provenance notes stay here, where the question is whether to
 * trust the data rather than which club to hit.
 *
 * All units in the drawing are metres, so the arcs cannot drift out of scale with the
 * shapes: a 100 m arc is a circle of radius 100 in the same coordinate space.
 */

/** Stroke widths and type sizes are metres too, so they scale with the hole. */
const SHAPE_STROKE_M = 1.2;
const ARC_STROKE_M = 1.1;
const LABEL_SIZE_M = 9;

/** Below this a press is a tap rather than a drag — a hand is never perfectly still. */
const DRAG_THRESHOLD_PX = 4;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

/** A shape's SVG `points` attribute ("x,y x,y ...") back into the points `pointInPolygon`
 * hit-tests against — the same metres space the shape itself was drawn in. */
const parseGuidePoints = (points: string): Point[] =>
  points
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((pair) => {
      const [x, y] = pair.split(',').map(Number);
      return { x, y };
    });

/**
 * Zoom and pan on top of the drawing's own fit, in the same shape the imagery view above it
 * uses — a multiplier on the fitted scale, and a shift expressed as a fraction of the fitted
 * frame rather than a pixel count. Pixels would be meaningless shared with a satellite canvas
 * of a different size; a fraction of "the frame that fits the hole" means the same thing on
 * both, which is what lets one caller drive both views from a single piece of state.
 */
export type GuideViewTransform = { zoom: number; panXFrac: number; panYFrac: number };

export const IDENTITY_GUIDE_VIEW: GuideViewTransform = { zoom: 1, panXFrac: 0, panYFrac: 0 };

export type HoleGuideCardProps = {
  holeNumber: number;
  tees: { lat: number; lon: number; label?: string | null }[];
  green: { lat: number; lon: number } | null;
  /** Every polygon on the course. The layout picks the ones reaching into this hole. */
  features: GuideFeature[];
  height?: number;
  /** Zoom and pan on top of the fitted framing. Defaults to the fit itself. */
  view?: GuideViewTransform;
  /**
   * Reports the pan a drag has reached so far, as the same frame-relative fraction `view`
   * takes — not a delta, the same convention `HoleMapCanvas`'s `onPan` uses. Supplying it is
   * what makes the drawing draggable; omitted, it is a static picture.
   */
  onPan?: (panXFrac: number, panYFrac: number) => void;
  /** Wired to the same zoom-control cluster the imagery view offers, when supplied. */
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  onResetZoom?: () => void;
  /**
   * A tap that landed on a drawn shape reports that shape's id — the topmost one, when more
   * than one is stacked at that point. The caller already holds the full analysis record
   * for every feature, so there is nothing more useful this component could hand back.
   * Omitted, a tap does nothing.
   */
  onShapePress?: (shapeId: string) => void;
};

export function HoleGuideCard({
  holeNumber,
  tees,
  green,
  features,
  height = 420,
  view = IDENTITY_GUIDE_VIEW,
  onPan,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onShapePress,
}: HoleGuideCardProps) {
  const { t } = useI18n();
  const guide = useMemo(
    () => buildHoleGuide({ tees, green, features }),
    [features, green, tees]
  );

  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
  const onFrameLayout = (event: LayoutChangeEvent) => {
    const { width: w, height: h } = event.nativeEvent.layout;
    const next = { width: Math.round(w), height: Math.round(h) };
    setFrameSize((current) =>
      current.width === next.width && current.height === next.height ? current : next
    );
  };

  /** The gesture in progress: where it started, the pan it started from, and whether it has
   * moved far enough yet to count as a drag rather than a tap. */
  const gestureRef = useRef<{
    startX: number;
    startY: number;
    startPanXFrac: number;
    startPanYFrac: number;
    travelled: boolean;
  } | null>(null);

  const framePointOf = (event: GestureResponderEvent) =>
    pressPointInElement(
      event.nativeEvent as PressPointEvent,
      event.currentTarget as unknown as MeasurableElement
    );

  const zoom = clamp(view.zoom, MIN_VIEW_ZOOM, MAX_VIEW_ZOOM);
  // A pan handler alone used to be what made this interactive; a shape-tap handler now does
  // too, so a caller that only wants taps (no dragging) does not also have to wire up `onPan`.
  const interactive = !!onPan || !!onShapePress;

  if (!guide) {
    return (
      <View style={styles.card} testID="hole-guide-empty">
        <Text style={styles.emptyText}>{t('courseAnalysis.guide.unavailable')}</Text>
      </View>
    );
  }

  const [baseMinX, baseMinY, baseWidth, baseHeight] = guide.viewBox.split(' ').map(Number);
  // Panning is bounded the same way the imagery view bounds it: zero room at the fit, growing
  // with the zoom, so the drawing can never be dragged entirely out of frame.
  const maxPanFrac = (zoom - 1) / 2;
  const panXFrac = clamp(view.panXFrac, -maxPanFrac, maxPanFrac);
  const panYFrac = clamp(view.panYFrac, -maxPanFrac, maxPanFrac);
  const width = baseWidth / zoom;
  const heightM = baseHeight / zoom;
  // Subtracted, not added: `panXFrac` is how far the *content* has shifted (positive =
  // right, matching `HoleMapCanvas`), and moving the content right means sliding this
  // viewBox's own window left over it.
  //
  // Scaled by `width` (the *zoomed* span), not `baseWidth` — the on-screen pixel size of
  // this frame never changes, only how many metres it shows, so `panXFrac` has to be
  // converted through however many metres a pixel is worth *right now*. Scaling by the
  // unzoomed `baseWidth` instead made the same `panXFrac` cover more of the (shrunken,
  // zoomed-in) view the further in you zoomed, which is the imagery-vs-drawing mismatch
  // this is fixing — `HoleMapCanvas`'s own pan is a fixed pixel count, so it never had
  // that problem to begin with.
  const minX = baseMinX + baseWidth / 2 - panXFrac * width - width / 2;
  const minY = baseMinY + baseHeight / 2 - panYFrac * heightM - heightM / 2;
  const viewBox = `${minX} ${minY} ${width} ${heightM}`;

  // Two different emptinesses, and naming the wrong one sends the reader after the wrong
  // problem. A course with hazards but no cover has been analysed by an older build and
  // wants re-running; a course with no boundary at all has never been analysed, and no
  // amount of re-running the cover classes will change that. The first version of this
  // reported "no fairway, no trees" for both, which pointed straight past the real cause.
  const nothingMapped = guide.shapes.length === 0;
  const missingCover = nothingMapped
    ? []
    : ['fairway', 'trees'].filter((kind) => !guide.kinds.includes(kind));

  const onGestureStart = (event: GestureResponderEvent) => {
    if (!interactive) return;
    const point = framePointOf(event);
    if (!point) return;
    gestureRef.current = {
      startX: point.x,
      startY: point.y,
      startPanXFrac: view.panXFrac,
      startPanYFrac: view.panYFrac,
      travelled: false,
    };
  };

  const onGestureMove = (event: GestureResponderEvent) => {
    const gesture = gestureRef.current;
    if (!gesture || !(frameSize.width > 0) || !(frameSize.height > 0)) return;
    const point = framePointOf(event);
    if (!point) return;
    const dx = point.x - gesture.startX;
    const dy = point.y - gesture.startY;
    if (Math.hypot(dx, dy) >= DRAG_THRESHOLD_PX) gesture.travelled = true;
    if (!onPan) return;
    // Same sign as `HoleMapCanvas`'s own `onPan` — see this prop's doc — so a caller
    // driving both views from one shared value gets a rightward drag on either one moving
    // both the same way, not opposite ways. Divided by the frame's *own* pixel size only —
    // not by zoom too. `HoleMapCanvas`'s pan is a raw screen-pixel count, tracked 1:1 with
    // the finger at every zoom; the matching `panXFrac / zoom` term lives in the viewBox
    // maths above, not here, so the two stay in step. Dividing by zoom on both ends (an
    // earlier version of this) cancelled out into the same on-screen distance as this one at
    // the fit, but grew the drawing's own pan *faster than the imagery's* the further in you
    // zoomed — a drag that looked identical on both views at zoom 1 moved the drawing
    // noticeably further once zoomed in.
    onPan(gesture.startPanXFrac + dx / frameSize.width, gesture.startPanYFrac + dy / frameSize.height);
  };

  /**
   * A press that never travelled is a tap: hit-tested against the shapes actually drawn,
   * topmost first, in the same metres space `viewBox` already places them in. Whichever
   * shape the tap landed on reports its own id — the caller already has the full analysis
   * record for it, so there is nothing more for this component to know or pass along.
   */
  const onGestureEnd = (event: GestureResponderEvent) => {
    const gesture = gestureRef.current;
    gestureRef.current = null;
    if (!gesture || gesture.travelled || !onShapePress) return;
    const point = framePointOf(event);
    if (!point || !(frameSize.width > 0) || !(frameSize.height > 0)) return;
    const tapped = {
      x: minX + (point.x / frameSize.width) * width,
      y: minY + (point.y / frameSize.height) * heightM,
    };
    for (let index = guide.shapes.length - 1; index >= 0; index -= 1) {
      const shape = guide.shapes[index];
      if (pointInPolygon(tapped, parseGuidePoints(shape.points))) {
        onShapePress(shape.id);
        return;
      }
    }
  };

  const responderProps = interactive
    ? {
        onStartShouldSetResponder: () => true,
        onMoveShouldSetResponder: () => true,
        onResponderGrant: onGestureStart,
        onResponderMove: onGestureMove,
        onResponderRelease: onGestureEnd,
        onResponderTerminate: onGestureEnd,
        onResponderTerminationRequest: () => false,
      }
    : null;

  return (
    <View style={styles.card} testID="hole-guide">
      <View style={styles.header}>
        <Text style={styles.title}>{t('courseAnalysis.guide.title', { hole: holeNumber })}</Text>
        <Text style={styles.length} testID="hole-guide-length">
          {t('courseAnalysis.guide.length', { metres: guide.holeLengthM })}
        </Text>
      </View>

      <View
        style={[styles.frame, { height }]}
        onLayout={onFrameLayout}
        testID="hole-guide-frame"
        {...responderProps}
      >
        <Svg width="100%" height="100%" viewBox={viewBox} testID="hole-guide-svg">
          <Rect x={minX} y={minY} width={width} height={heightM} fill={ROUGH_FILL} />

          {guide.shapes.map((shape) => {
            const palette = FILLS[shape.kind];
            if (!palette) return null;
            return (
              <Polygon
                key={shape.id}
                points={shape.points}
                fill={palette.fill}
                stroke={palette.stroke}
                strokeWidth={SHAPE_STROKE_M}
                strokeLinejoin="round"
              />
            );
          })}

          {/* Drawn before the arcs so a number is never sitting under the line of play. */}
          <Line
            x1={guide.playLine.x1}
            y1={guide.playLine.y1}
            x2={guide.playLine.x2}
            y2={guide.playLine.y2}
            stroke={alpha(appColors.text.primary, 0.45)}
            strokeWidth={ARC_STROKE_M}
            strokeDasharray="6,5"
          />

          {/* Circles rather than path arcs: the viewBox clips them at the edges of the
              frame, which is exactly how a guide prints them — running off both sides. */}
          {guide.arcs.map((arc) => (
            <Circle
              key={arc.distanceM}
              cx={arc.cx}
              cy={arc.cy}
              r={arc.r}
              fill="none"
              stroke={appColors.accent.gold}
              strokeWidth={ARC_STROKE_M}
              strokeDasharray="5,4"
            />
          ))}

          {guide.arcs.map((arc) => (
            <SvgText
              key={`label-${arc.distanceM}`}
              x={arc.labelX + 4}
              y={arc.labelY - 3}
              fill={appColors.accent.gold}
              fontSize={LABEL_SIZE_M}
              fontWeight="bold"
            >
              {String(arc.distanceM)}
            </SvgText>
          ))}

          {guide.greenFront ? (
            <Circle
              cx={guide.greenFront.x}
              cy={guide.greenFront.y}
              r={2.5}
              fill={appColors.accent.gold}
              testID="hole-guide-front-edge"
            />
          ) : null}

          {guide.tees.map((tee, index) => (
            <Rect
              key={`tee-${index}`}
              x={tee.x - 3}
              y={tee.y - 2}
              width={6}
              height={4}
              rx={1}
              fill={appColors.text.primary}
              stroke={appColors.surface.tintDeep}
              strokeWidth={0.8}
            />
          ))}
        </Svg>

        {onZoomIn && onZoomOut && onResetZoom ? (
          <ZoomControls
            zoom={zoom}
            onZoomIn={onZoomIn}
            onZoomOut={onZoomOut}
            onReset={onResetZoom}
            testIDPrefix="hole-guide"
          />
        ) : null}
      </View>

      <View style={styles.legend}>
        {guide.kinds.map((kind) => (
          <View key={kind} style={styles.legendItem}>
            <View style={[styles.legendSwatch, { backgroundColor: FILLS[kind]?.fill ?? appColors.neutral.white }]} />
            <Text style={styles.legendLabel}>{t(`courseAnalysis.kind.${kind}`)}</Text>
          </View>
        ))}
        {guide.arcs.length > 0 ? (
          <Text style={styles.legendNote}>{t('courseAnalysis.guide.arcsNote')}</Text>
        ) : null}
      </View>

      {nothingMapped ? (
        <Text style={styles.notice} testID="hole-guide-nothing-mapped">
          {t('courseAnalysis.guide.nothingMapped')}
        </Text>
      ) : missingCover.length > 0 ? (
        <Text style={styles.notice} testID="hole-guide-missing-cover">
          {t('courseAnalysis.guide.missingCover', {
            kinds: missingCover.map((kind) => t(`courseAnalysis.kind.${kind}`)).join(', '),
          })}
        </Text>
      ) : null}

      <Text style={styles.notice}>{t('courseAnalysis.guide.derivedNotice')}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    backgroundColor: appColors.surface.alt,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    padding: 12,
    gap: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
  },
  title: { color: appColors.text.primary, fontSize: 16, fontWeight: '700' },
  length: {
    color: appColors.accent.primary,
    fontSize: 14,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  frame: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    overflow: 'hidden',
    backgroundColor: ROUGH_FILL,
  },
  legend: { flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center', gap: 12 },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 5 },
  legendSwatch: { width: 12, height: 12, borderRadius: 3 },
  legendLabel: { color: appColors.text.secondary, fontSize: 12 },
  legendNote: { color: appColors.text.muted, fontSize: 11, flexShrink: 1 },
  emptyText: {
    color: appColors.text.secondary,
    fontSize: 13,
    textAlign: 'center',
    paddingVertical: 24,
  },
  notice: { color: appColors.text.muted, fontSize: 11, lineHeight: 15 },
});

export default HoleGuideCard;
